import type { ReactNode } from 'react';

const dict = {
  en: {
    doSubmit: 'Send',
    doLogIn: 'Log in',
    doRegister: 'Register',
    doForgotPassword: 'Forgot Password',
    username: 'Username',
    email: 'Email',
    usernameOrEmail: 'Username or email',
    password: 'Password',
  },
  nl: {
    doSubmit: 'Verzenden',
    doLogIn: 'Inloggen',
    doRegister: 'Registreren',
    doForgotPassword: 'Wachtwoord vergeten',
    username: 'Gebruikersnaam',
    email: 'E-mailadres',
    usernameOrEmail: 'Gebruikersnaam of e-mail',
    password: 'Wachtwoord',
  },
} as const;

type Lang = keyof typeof dict;
type Keys = keyof (typeof dict)['en'];

function replaceIndexedPlaceholders(tpl: string, args: Array<string | number>): string {
  return tpl.replace(/\{(\d+)}/g, (_, i: string) => {
    const idx = Number(i);
    return idx in args ? String(args[idx]) : `{${i}}`;
  });
}

export type I18n = {
  msg: (key: Keys) => ReactNode;
  msgStr: (key: Keys) => string;

  // ⬇️ v7 verwacht deze twee
  advancedMsg: (key: Keys, ...args: Array<ReactNode | string | number>) => ReactNode;
  advancedMsgStr: (key: Keys, ...args: Array<string | number>) => string;

  currentLanguageTag: Lang;

  // ⬇️ BELANGRIJK: type exact zoals v7 het verwacht
  changeLocale: (newLanguageTag: string) => never;

  labelBySupportedLanguageTag: Record<Lang, string>;
};

export const i18n: I18n = {
  currentLanguageTag: 'nl',

  msg: key => dict.nl[key] ?? (key as string),
  msgStr: key => dict.nl[key] ?? (key as string),

  advancedMsg: (key, ...args) => {
    const base = dict.nl[key] ?? (key as string);
    const strArgs = args.map(a => (typeof a === 'string' || typeof a === 'number' ? a : ''));
    return replaceIndexedPlaceholders(base, strArgs);
  },
  advancedMsgStr: (key, ...args) => {
    const base = dict.nl[key] ?? (key as string);
    return replaceIndexedPlaceholders(base, args);
  },

  changeLocale: (newLanguageTag: string): never => {
    throw new Error(`Locale change not implemented (requested: ${newLanguageTag})`);
  },

  labelBySupportedLanguageTag: { en: 'English', nl: 'Nederlands' },
};
